import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation } from "@apollo/client";
import { SEND_MESSAGE } from "../../utils/GraphQL/mutations";
import capitalizeFirstLetters from "../../utils/formatters/capitalizeFirstLetters";
import phoneNumberFormat from "../../utils/formatters/phoneNumberFormat";
import FormTestV2 from "./quoteFormInputCheckV2";

/**
 * All Request-a-Quote state + handlers, extracted verbatim from
 * pages/GetAQuote/GetAQuote.jsx so the v2 quote form behaves identically to
 * prod. Both v2 skins (dark/light) consume this one hook → no drift.
 *
 * @param {object} opts
 * @param {string} [opts.submitContextUri] - if set (e.g. "/tgraphql"), the
 *   SEND_MESSAGE mutation is routed to that endpoint per-operation (used by the
 *   dev preview to land test leads in the TEST db). Omit for prod (uses the
 *   global client endpoint) so this hook is a clean drop-in for the live page.
 */
const useQuoteFormV2 = ({ submitContextUri } = {}) => {
  const [sendMessage] = useMutation(SEND_MESSAGE);
  const [gotQuoted, setGotQuoted] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [isSticky, setIsSticky] = useState(true);
  const [modalText, setModalText] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState({
    nameInput: "",
    emailInput: "",
    cityInput: "",
    phoneNumberInput: "",
    numberType: "",
    textConsent: true,
    intExtChoice: "Exterior",
    discountChoice: "None",
    heardAboutUs: "",
    otherInput: "",
    imageIdArr: [],
  });

  // Google Places Autocomplete state
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [cityHighlight, setCityHighlight] = useState(-1);
  const autocompleteService = useRef(null);
  const cityInputRef = useRef(null);

  useEffect(() => {
    // Load Google Places script (shared id guard — do NOT re-key; prod injects
    // the same id="google-places-script", so this reuses it if present).
    if (!document.getElementById("google-places-script")) {
      const script = document.createElement("script");
      script.id = "google-places-script";
      script.src =
        "https://maps.googleapis.com/maps/api/js?key=AIzaSyAajOAIHFb3kziY3a1UVz5Ler1y8nGxCr4&libraries=places";
      script.async = true;
      script.onload = () => {
        if (window.google?.maps?.places) {
          autocompleteService.current =
            new window.google.maps.places.AutocompleteService();
        }
      };
      document.head.appendChild(script);
    } else if (window.google?.maps?.places) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
  }, []);

  const fetchCitySuggestions = useCallback((input) => {
    if (!autocompleteService.current || input.length < 2) {
      setCitySuggestions([]);
      return;
    }
    autocompleteService.current.getPlacePredictions(
      {
        input,
        types: ["(cities)"],
        componentRestrictions: { country: "us" },
      },
      (predictions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setCitySuggestions(predictions);
        } else {
          setCitySuggestions([]);
        }
      }
    );
  }, []);

  const handleCitySelect = (description) => {
    setFormState((prev) => ({ ...prev, cityInput: description }));
    setCitySuggestions([]);
    setShowCitySuggestions(false);
    setCityHighlight(-1);
  };

  const handleCityKeyDown = (e) => {
    if (!showCitySuggestions || citySuggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCityHighlight((prev) =>
        prev < citySuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCityHighlight((prev) =>
        prev > 0 ? prev - 1 : citySuggestions.length - 1
      );
    } else if (e.key === "Enter" && cityHighlight >= 0) {
      e.preventDefault();
      handleCitySelect(citySuggestions[cityHighlight].description);
    } else if (e.key === "Escape") {
      setShowCitySuggestions(false);
      setCityHighlight(-1);
    }
  };

  // Error helper-text triggers on inputs when they fail checks
  const [errorState, setErrorState] = useState({
    emailInput: false,
    cityInput: false,
    phoneNumberInput: false,
    otherInput: false,
  });

  const changeLoading = async (newState) => {
    setLoadingSaved(newState);
    return;
  };

  // form validation class (snapshot of current state — cheap per render)
  const formInputCheck = new FormTestV2(formState);

  const handleChange = async (event) => {
    let value = event.target.value;

    if (event.target.name === "nameInput") {
      value = await capitalizeFirstLetters(value);
    }

    if (event.target.name === "phoneNumberInput") {
      value = await phoneNumberFormat(value);
    }

    if (event.target.name === "cityInput") {
      setFormState({ ...formState, cityInput: value });
      fetchCitySuggestions(value);
      setShowCitySuggestions(true);
      setCityHighlight(-1);
      return;
    }

    if (event.target.name === "numberType") {
      setFormState({
        ...formState,
        [event.target.name]: value,
      });
      return;
    }

    if (event.target.name === "textConsent") {
      setFormState({
        ...formState,
        textConsent: event.target.checked,
      });
      return;
    }

    setFormState({
      ...formState,
      [event.target.name]: value,
    });
  };

  const checkError = async (event) => {
    let value = event.target.value;

    if (event.target.name === "otherInput") {
      value = await formInputCheck.testOther();
      setErrorState({ ...errorState, [event.target.name]: !value });
      return;
    }

    if (event.target.name === "numberType") {
      value = await formInputCheck.testNumberType();
    }

    if (value === "") {
      setErrorState({ ...errorState, [event.target.name]: false });
      return;
    }

    if (event.target.name === "emailInput") {
      value = await formInputCheck.testEmail();
    }

    if (event.target.name === "phoneNumberInput") {
      value = await formInputCheck.testPhone();
    }

    setErrorState({ ...errorState, [event.target.name]: !value });
  };

  const clearError = (event) => {
    const name = event.target?.name || event;
    setErrorState({ ...errorState, [name]: false });
  };

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  // server call for sending form submission
  const sendContactEmail = async (event) => {
    event.preventDefault();
    const validForm = await formInputCheck.formValid();

    if (validForm) {
      setGotQuoted(true);

      setModalText({
        title: "Thank you for contacting BDR Services!",
        description:
          "We will go over your information and get a quote worked up for you. If you have any questions please feel free to contact us!",
        home: true,
        time: 8000,
      });
      handleOpen();

      sendMessage({
        variables: formState,
        ...(submitContextUri
          ? { context: { uri: submitContextUri } }
          : {}),
      });
    }

    if (!validForm) {
      setModalText({
        title: "Oops...",
        description: "All form details need to be completed",
        home: false,
        time: 2000,
      });
      handleOpen();
    }

    return;
  };

  const changePhotoIds = (photoId) => {
    setFormState((prevState) => ({
      ...prevState,
      imageIdArr: [...prevState.imageIdArr, photoId],
    }));
  };

  const changeSticky = (newState) => setIsSticky(newState);

  return {
    // state
    formState,
    errorState,
    modalOpen,
    modalText,
    loadingSaved,
    isSticky,
    gotQuoted,
    citySuggestions,
    showCitySuggestions,
    cityHighlight,
    cityInputRef,
    // setters needed by the city autocomplete UI
    setShowCitySuggestions,
    setCityHighlight,
    // handlers
    handleChange,
    checkError,
    clearError,
    handleCitySelect,
    handleCityKeyDown,
    sendContactEmail,
    changePhotoIds,
    changeLoading,
    changeSticky,
    handleClose,
  };
};

export default useQuoteFormV2;
